import DocViewer from "@cyntler/react-doc-viewer";
import { pdf } from "@react-pdf/renderer";
import type { Meeting, Team, Tenant, User } from "@repo/types";
import { AgendaDocument } from "@repo/ui/components/Base/AgendaDocument";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useEffect, useState } from "react";
import { DocViewerRenderers } from "../common/DocViewerRenderers";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { pdfService } from "@/services/pdfService";
import { promiseGuts, type PromiseGuts } from "@repo/lib/promiseGuts";
import { apiService } from "@/services/apiService";
import { userStore } from "@/stores/userStore";
import { config } from "@/config";
import { documentRepo } from "@/repositories/documentRepo";
import { teamRepo } from "@/repositories/teamRepo";

export interface MeetingPreviewDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  tenant: Tenant;
  meeting: Meeting;
  team: Team;
  userMap: Record<string, User>;
  allowShowDetails?: boolean;
}

let lock: PromiseGuts<void> | null = null;
const emptyBlob = new Blob([], { type: "application/octet-stream" });

export const MeetingPreviewDialog = ({
  open,
  setOpen,
  title,
  tenant,
  meeting,
  team: teamData,
  userMap,
  allowShowDetails = false,
}: MeetingPreviewDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [showFullPacket, setShowFullPacket] = useState(false);
  const [includeConfidential, setIncludeConfidential] = useState(false);

  const allowConfidential = teamRepo.canViewConfidentialDocuments(
    userStore.getState().id,
    teamData,
    true
  );

  const loadPdf = useFutureCallback(
    async ({
      aborter,
      showDetails = false,
      showFullPacket = false,
      includeConfidential = false,
    }: {
      aborter: { cancel: boolean };
      showDetails?: boolean;
      showFullPacket?: boolean;
      includeConfidential?: boolean;
    }) => {
      await AgendaDocument.readyPromise;

      if (lock) await lock.promise;
      lock = promiseGuts();

      console.log(0);

      let blob = await pdf(
        <AgendaDocument
          tenant={tenant}
          meeting={meeting}
          team={teamData}
          letterHead={
            meeting.agenda?.letterHead ?? meeting.letterHead ?? undefined
          }
          numberFormat={tenant.numberFormat}
          apiUrl={config.urls.api}
          augmented={showDetails}
          userMap={userMap}
        />
      ).toBlob();

      lock.resolve();
      lock = null;

      if (aborter.cancel) return emptyBlob;

      // generate combined packet
      if (showFullPacket) {
        const agendaPDF = await pdfService.pdfFromBlob(blob);

        const canViewConfidential = allowConfidential && includeConfidential;

        console.log(1);

        // collect PDF files from documents
        const attachments: {
          id: string;
          url: string;
          label: string;
          confidential: boolean;
        }[] = [];

        for (const agendaModule of meeting.agenda?.agendaModules ?? []) {
          for (const agendaItem of agendaModule.agendaItems ?? []) {
            for (const doc of agendaItem.documents ?? []) {
              if (!(doc.visibleOnAgenda ?? true)) continue;
              if (!canViewConfidential && doc.confidential) continue;

              const attachment = documentRepo.getBestAttachment(doc);
              if (!attachment?.url) continue;
              if (attachment.extension?.toLowerCase() !== "pdf") continue;

              attachments.push({
                id: doc.id,
                url: attachment.url,
                label: doc.name,
                confidential: !!doc.confidential,
              });
            }
          }
        }

        console.log(2);

        // download attachments
        const attachmentPDFs = (
          await Promise.all(
            attachments.map(async ({ id, url, label, confidential }) => {
              try {
                if (canViewConfidential && confidential) {
                  const { data } = await apiService.v1.documentDownloadUrl(
                    tenant.id,
                    id
                  );
                  if (!data || aborter.cancel) return null;
                  url = data;
                }

                const response = await fetch(url);
                if (aborter.cancel) return null;

                const buffer = await response.arrayBuffer();
                if (aborter.cancel) return null;

                return {
                  pdf: await pdfService.pdfFromArrayBuffer(buffer),
                  label,
                };
              } catch (err) {
                console.warn(err);
                return null;
              }
            })
          )
        ).filter((pdf) => !!pdf);

        if (aborter.cancel) return emptyBlob;

        console.log(3);

        const combined = await pdfService.mergePDFs([
          { pdf: agendaPDF, label: "Agenda" },
          ...attachmentPDFs,
        ]);

        if (aborter.cancel) return emptyBlob;

        console.log(4);

        blob = await pdfService.toBlob(combined);
        if (aborter.cancel) return emptyBlob;

        console.log(5);
      }

      return blob;
    }
  );

  useEffect(() => {
    let cancel = false;
    let url = "";
    const aborter = { cancel: false };

    if (open) {
      setLoading(true);
      setError("");
      setPdfUrl("");
      loadPdf({
        aborter,
        showDetails: showDetails && allowShowDetails,
        showFullPacket,
        includeConfidential,
      })
        .then((blob) => {
          if (cancel) return;

          url = URL.createObjectURL(blob);
          setPdfUrl(url);
          setLoading(false);
        })
        .catch((err) => {
          setError((err as Error).message);
          setLoading(false);
        });
    } else {
      setPdfUrl("");
    }

    return () => {
      cancel = true;
      aborter.cancel = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [
    loadPdf,
    showDetails,
    showFullPacket,
    includeConfidential,
    allowShowDetails,
    open,
  ]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[90vh]">
          <div className="flex-grow flex flex-col gap-4 overflow-hidden">
            <div className="flex flex-row justify-start gap-6">
              {allowShowDetails && (
                <CheckboxInput
                  wrapperStyle="w-auto"
                  orientation="horizontal"
                  label="Show Additional Details"
                  checked={showDetails}
                  onCheckedChange={(checked) => setShowDetails(!!checked)}
                />
              )}
              <CheckboxInput
                wrapperStyle="w-auto"
                orientation="horizontal"
                label="Show Full Packet"
                checked={showFullPacket}
                onCheckedChange={(checked) => setShowFullPacket(!!checked)}
              />
              {allowConfidential && showFullPacket && (
                <CheckboxInput
                  wrapperStyle="w-auto"
                  orientation="horizontal"
                  label="Include Confidential Documents"
                  checked={includeConfidential}
                  onCheckedChange={(checked) =>
                    setIncludeConfidential(!!checked)
                  }
                />
              )}
            </div>
            {pdfUrl ? (
              <DocViewer
                key={pdfUrl}
                prefetchMethod="GET"
                pluginRenderers={DocViewerRenderers}
                documents={[{ uri: pdfUrl }]}
                config={{
                  header: { disableHeader: true },
                  loadingRenderer: {
                    overrideComponent: ({ document, fileName }) => {
                      const fileText = fileName || document?.fileType || "";
                      return (
                        <div className="text-center p-2 truncate">
                          Loading
                          {fileText && (
                            <>
                              <br />({fileText})
                            </>
                          )}
                        </div>
                      );
                    },
                  },
                }}
              />
            ) : loading || error ? (
              <div className="flex flex-row justify-center items-center w-full min-h-[60vh]">
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" /> Loading...
                  </>
                ) : (
                  <p className="text-destructive">{error}</p>
                )}
              </div>
            ) : null}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
